import type {
  Box,
  QueryPlanNode,
  QueryPlanSpec,
  ResolvedFigure,
} from '../model.js';
import {
  box,
  finishFigure,
  point,
  roundGeometry,
  SceneBuilder,
  validateViewport,
} from '../scene-builder.js';

const TOP = 54;
const BOTTOM = 34;
const PHONE_BREAKPOINT = 390;
const FORBIDDEN_PRESENTATION_KEYS = new Set([
  'x', 'y', 'cx', 'cy', 'x1', 'x2', 'y1', 'y2', 'd', 'points', 'box',
  'fill', 'stroke', 'color', 'fontSize', 'fontFamily', 'duration',
]);

interface PlanLayout {
  nodeById: Map<string, QueryPlanNode>;
  childrenById: Map<string, string[]>;
  depthById: Map<string, number>;
  orderedNodeIds: string[];
  xById: Map<string, number>;
  maxDepth: number;
}

export function queryPlanNodeId(nodeId: string): string {
  return `plan.node.${nodeId}`;
}

export function queryPlanEdgeId(from: string, to: string): string {
  return `plan.edge.${from}.${to}`;
}

/**
 * Compiles a coordinate-free operator tree into a stable schematic.
 *
 * Edges point from a child operator to its parent: rows flow upward through the
 * plan until the root produces the result. Children are sorted canonically by
 * semantic ID, so equivalent specs compile identically even when their input
 * arrays arrive in a different order.
 */
export function compileQueryPlan(spec: QueryPlanSpec): ResolvedFigure {
  const layout = validateQueryPlan(spec);
  const builder = new SceneBuilder();
  const { width, height } = spec.viewport;
  const narrow = width <= PHONE_BREAKPOINT;
  const margin = width <= 320 ? 12 : narrow ? 16 : 24;
  const usableWidth = width - margin * 2;
  const maxNodesAtLevel = Math.max(
    ...Array.from({ length: layout.maxDepth + 1 }, (_, depth) =>
      layout.orderedNodeIds.filter((id) => layout.depthById.get(id) === depth).length,
    ),
  );
  const gap = narrow ? 8 : 14;
  const nodeWidth = clamp(
    (usableWidth - gap * (maxNodesAtLevel - 1)) / maxNodesAtLevel,
    64,
    narrow ? 116 : 132,
  );
  const nodeHeight = narrow ? 70 : 76;
  const diagramBottom = height - BOTTOM - nodeHeight;
  const levelStep = layout.maxDepth === 0 ? 0 : (diagramBottom - TOP) / layout.maxDepth;
  const nodeBounds = new Map<string, Box>();
  const warnings: string[] = [];

  builder.text('plan.eyebrow', spec.id, 'annotation', point(margin, 25), 'QUERY PLAN · ROWS FLOW ↑', {
    size: 10,
    mono: true,
    weight: 700,
    uppercase: true,
  });

  for (const nodeId of layout.orderedNodeIds) {
    const depth = layout.depthById.get(nodeId)!;
    const centerX = margin + nodeWidth / 2 + layout.xById.get(nodeId)! * (usableWidth - nodeWidth);
    const centerY = TOP + depth * levelStep + nodeHeight / 2;
    nodeBounds.set(nodeId, box(centerX - nodeWidth / 2, centerY - nodeHeight / 2, nodeWidth, nodeHeight));
  }

  // Edges are emitted first so nodes remain visually on top of routed paths.
  const canonicalEdges = [...spec.edges].sort(compareEdges);
  for (const edge of canonicalEdges) {
    const child = layout.nodeById.get(edge.from)!;
    const childBounds = nodeBounds.get(edge.from)!;
    const parentBounds = nodeBounds.get(edge.to)!;
    const semanticId = queryPlanEdgeId(edge.from, edge.to);
    const start = point(childBounds.x + childBounds.width / 2, childBounds.y);
    const end = point(parentBounds.x + parentBounds.width / 2, parentBounds.y + parentBounds.height);
    const middleY = roundGeometry((start.y + end.y) / 2);
    const path = `M ${n(start.x)} ${n(start.y)} V ${n(middleY)} H ${n(end.x)} V ${n(end.y)}`;
    const rowLabel = edge.label?.trim() || `${formatMetric(child.estimatedRows)} rows`;
    const labelX = (start.x + end.x) / 2;
    const labelWidth = clamp(rowLabel.length * (narrow ? 5.1 : 5.7) + 14, 48, narrow ? 92 : 122);
    const shownLabel = compact(rowLabel, Math.max(7, Math.floor((labelWidth - 12) / (narrow ? 5.1 : 5.7))));
    const edgeBounds = touchBounds(start, end, spec.viewport.width, spec.viewport.height);

    builder.path(`${semanticId}.path`, semanticId, 'series', path, { markerEnd: true, label: rowLabel });
    builder.rect(`${semanticId}.label-plate`, semanticId, 'surface', box(labelX - labelWidth / 2, middleY - 10, labelWidth, 19), {
      radius: 9,
      label: rowLabel,
    });
    builder.text(`${semanticId}.label`, semanticId, 'annotation', point(labelX, middleY + 3), shownLabel, {
      size: narrow ? 8 : 9,
      mono: true,
      weight: 700,
      align: 'middle',
    });
    builder.anchor(`${semanticId}.source`, semanticId, start, 'up');
    builder.anchor(`${semanticId}.mid`, semanticId, point(labelX, middleY));
    builder.anchor(`${semanticId}.target`, semanticId, end, 'up');
    builder.hit(semanticId, edgeBounds);
    builder.relation(
      `${semanticId}.row-flow`,
      'row-flow',
      queryPlanNodeId(edge.from),
      queryPlanNodeId(edge.to),
      `${rowLabel} flow from ${child.label} to ${layout.nodeById.get(edge.to)!.label}`,
    );

    if (shownLabel !== rowLabel) warnings.push(`Edge label compacted: ${edge.from} → ${edge.to}`);
  }

  for (const nodeId of layout.orderedNodeIds) {
    const node = layout.nodeById.get(nodeId)!;
    const bounds = nodeBounds.get(nodeId)!;
    const semanticId = queryPlanNodeId(node.id);
    const maxChars = Math.max(6, Math.floor((bounds.width - 14) / (narrow ? 5.3 : 6.1)));
    const lines = fitLines(node.label, maxChars, 2);
    const labelY = bounds.y + (lines.length === 1 ? 33 : 25);

    builder.rect(`${semanticId}.surface`, semanticId, 'operator', bounds, {
      radius: 6,
      label: `${node.label}; estimated ${node.estimatedRows} rows; cost ${node.cost}`,
      interactive: true,
    });
    builder.text(`${semanticId}.kind`, semanticId, kindRole(node.kind), point(bounds.x + 9, bounds.y + 14), node.kind.toUpperCase(), {
      size: narrow ? 7 : 8,
      mono: true,
      weight: 700,
      uppercase: true,
    });
    lines.forEach((line, index) => {
      builder.text(`${semanticId}.label.${index}`, semanticId, 'structure', point(bounds.x + bounds.width / 2, labelY + index * 11), line, {
        size: narrow ? 8.5 : 10,
        mono: true,
        weight: 700,
        align: 'middle',
      });
    });
    builder.text(`${semanticId}.metrics`, semanticId, 'annotation', point(bounds.x + bounds.width / 2, bounds.y + bounds.height - 9), `r ${formatMetric(node.estimatedRows)} · c ${formatCost(node.cost)}`, {
      size: narrow ? 7 : 8,
      mono: true,
      weight: 700,
      align: 'middle',
    });
    builder.anchor(`${semanticId}.top`, semanticId, point(bounds.x + bounds.width / 2, bounds.y), 'up');
    builder.anchor(`${semanticId}.center`, semanticId, point(bounds.x + bounds.width / 2, bounds.y + bounds.height / 2));
    builder.anchor(`${semanticId}.bottom`, semanticId, point(bounds.x + bounds.width / 2, bounds.y + bounds.height), 'down');
    builder.hit(semanticId, bounds);

    if (lines.some((line) => line.includes('…'))) warnings.push(`Operator label compacted: ${node.id}`);
  }

  if (nodeWidth < 76) warnings.push('Operator cards use the compact phone policy at this width.');
  if (levelStep - nodeHeight < 44) warnings.push('Plan levels are approaching the schematic density floor.');

  const active = spec.focusNodeId ? [queryPlanNodeId(spec.focusNodeId)] : [queryPlanNodeId(spec.rootId)];
  active.push(...(spec.emphasis ?? []));

  return finishFigure(spec, builder, {
    title: 'A query plan transforms rows through operators',
    subtitle: 'Read upward from scans to the result: cardinality shrinks, combines, sorts, and aggregates.',
    state: spec.focusNodeId ? `focus:${queryPlanNodeId(spec.focusNodeId)}` : `root:${queryPlanNodeId(spec.rootId)}`,
    active,
    warnings: [...new Set(warnings)],
  });
}

/** Validates and returns the canonical tree facts used by the compiler. */
export function validateQueryPlan(spec: QueryPlanSpec): PlanLayout {
  validateViewport(spec.viewport.width, spec.viewport.height);
  assertCoordinateFree(spec);
  if (!spec.provenance.trim()) throw new Error('query-plan: provenance is required');
  if (spec.nodes.length < 2 || spec.nodes.length > 15) throw new Error('query-plan: require 2…15 operators');
  if (spec.edges.length !== spec.nodes.length - 1) throw new Error('query-plan: a tree requires exactly nodes.length - 1 edges');

  const nodeById = new Map<string, QueryPlanNode>();
  for (const node of spec.nodes) {
    if (!validSemanticId(node.id)) throw new Error(`query-plan: invalid node id ${node.id}`);
    if (nodeById.has(node.id)) throw new Error(`query-plan: duplicate node id ${node.id}`);
    if (!node.label.trim()) throw new Error(`query-plan: node ${node.id} requires a label`);
    if (!Number.isFinite(node.estimatedRows) || node.estimatedRows < 0) throw new Error(`query-plan: node ${node.id} has invalid estimatedRows`);
    if (!Number.isFinite(node.cost) || node.cost < 0) throw new Error(`query-plan: node ${node.id} has invalid cost`);
    if (node.kind === 'scan' && !node.relation?.trim()) throw new Error(`query-plan: scan ${node.id} requires a relation`);
    nodeById.set(node.id, node);
  }
  if (!nodeById.has(spec.rootId)) throw new Error(`query-plan: root ${spec.rootId} does not exist`);
  if (spec.focusNodeId && !nodeById.has(spec.focusNodeId)) throw new Error(`query-plan: focus node ${spec.focusNodeId} does not exist`);

  const parents = new Map<string, string[]>();
  const childrenById = new Map<string, string[]>([...nodeById.keys()].map((id) => [id, []]));
  const edgeIds = new Set<string>();
  for (const edge of spec.edges) {
    if (!nodeById.has(edge.from) || !nodeById.has(edge.to)) throw new Error(`query-plan: edge ${edge.from} → ${edge.to} references an unknown node`);
    if (edge.from === edge.to) throw new Error(`query-plan: self edge at ${edge.from}`);
    const edgeId = `${edge.from}\u0000${edge.to}`;
    if (edgeIds.has(edgeId)) throw new Error(`query-plan: duplicate edge ${edge.from} → ${edge.to}`);
    edgeIds.add(edgeId);
    const nodeParents = parents.get(edge.from) ?? [];
    nodeParents.push(edge.to);
    parents.set(edge.from, nodeParents);
    childrenById.get(edge.to)!.push(edge.from);
    if (edge.label !== undefined && !edge.label.trim()) throw new Error(`query-plan: edge ${edge.from} → ${edge.to} has a blank label`);
  }
  for (const children of childrenById.values()) children.sort((a, b) => a.localeCompare(b));
  if ((parents.get(spec.rootId) ?? []).length !== 0) throw new Error('query-plan: the root must not have a parent');
  for (const id of nodeById.keys()) {
    if (id !== spec.rootId && (parents.get(id) ?? []).length !== 1) {
      throw new Error(`query-plan: non-root node ${id} must have exactly one parent`);
    }
  }

  const depthById = new Map<string, number>([[spec.rootId, 0]]);
  const orderedNodeIds: string[] = [];
  const queue = [spec.rootId];
  while (queue.length) {
    const id = queue.shift()!;
    orderedNodeIds.push(id);
    const depth = depthById.get(id)!;
    for (const child of childrenById.get(id)!) {
      if (depthById.has(child)) throw new Error(`query-plan: cycle or repeated child detected at ${child}`);
      depthById.set(child, depth + 1);
      queue.push(child);
    }
  }
  if (orderedNodeIds.length !== spec.nodes.length) throw new Error('query-plan: plan must be one connected tree rooted at rootId');
  const maxDepth = Math.max(...depthById.values());
  if (maxDepth < 1 || maxDepth > 3) throw new Error('query-plan: schematic supports 2…4 operator levels');

  // Assign evenly spaced leaf slots, then center every operator over its
  // descendants. This is deterministic and prevents crossings in a tree.
  const leaves = orderedNodeIds.filter((id) => childrenById.get(id)!.length === 0).sort((a, b) => a.localeCompare(b));
  const leafX = new Map(leaves.map((id, index) => [id, leaves.length === 1 ? 0.5 : index / (leaves.length - 1)]));
  const xById = new Map<string, number>();
  const resolveX = (id: string): number => {
    const cached = xById.get(id);
    if (cached !== undefined) return cached;
    const children = childrenById.get(id)!;
    const value = children.length === 0
      ? leafX.get(id)!
      : children.reduce((sum, child) => sum + resolveX(child), 0) / children.length;
    xById.set(id, roundGeometry(value));
    return value;
  };
  resolveX(spec.rootId);

  return { nodeById, childrenById, depthById, orderedNodeIds, xById, maxDepth };
}

function assertCoordinateFree(value: unknown, path = 'spec'): void {
  if (!value || typeof value !== 'object') return;
  for (const [key, child] of Object.entries(value)) {
    if (FORBIDDEN_PRESENTATION_KEYS.has(key)) throw new Error(`query-plan: authored presentation field ${path}.${key} is forbidden`);
    assertCoordinateFree(child, `${path}.${key}`);
  }
}

function kindRole(kind: QueryPlanNode['kind']): 'slot' | 'pointer' | 'capacity' | 'active' | 'header' {
  if (kind === 'scan') return 'slot';
  if (kind === 'filter') return 'pointer';
  if (kind === 'join') return 'capacity';
  if (kind === 'sort') return 'header';
  return 'active';
}

function touchBounds(start: { x: number; y: number }, end: { x: number; y: number }, width: number, height: number): Box {
  const centerX = (start.x + end.x) / 2;
  const centerY = (start.y + end.y) / 2;
  const targetWidth = Math.max(44, Math.abs(start.x - end.x) + 16);
  const targetHeight = Math.max(44, Math.abs(start.y - end.y));
  const x = clamp(centerX - targetWidth / 2, 0, width - targetWidth);
  const y = clamp(centerY - targetHeight / 2, 0, height - targetHeight);
  return box(x, y, targetWidth, targetHeight);
}

function fitLines(value: string, maxChars: number, maxLines: number): string[] {
  const words = value.trim().replace(/\s+/g, ' ').split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const fitted = compact(word, maxChars);
    if (!current) current = fitted;
    else if (`${current} ${fitted}`.length <= maxChars) current += ` ${fitted}`;
    else {
      lines.push(current);
      current = fitted;
    }
  }
  if (current) lines.push(current);
  if (lines.length <= maxLines) return lines;
  const visible = lines.slice(0, maxLines);
  visible[maxLines - 1] = compact(lines.slice(maxLines - 1).join(' '), maxChars);
  return visible;
}

function compact(value: string, maxChars: number): string {
  if (value.length <= maxChars) return value;
  if (maxChars <= 3) return value.slice(0, maxChars);
  return `${value.slice(0, maxChars - 1)}…`;
}

function formatMetric(value: number): string {
  if (value >= 1_000_000) return `${trim(value / 1_000_000)}m`;
  if (value >= 1_000) return `${trim(value / 1_000)}k`;
  return Number.isInteger(value) ? String(value) : trim(value);
}

function formatCost(value: number): string {
  return value >= 1_000 ? formatMetric(value) : trim(value);
}

function trim(value: number): string {
  return value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2).replace(/\.0+$|(?<=\.[0-9])0+$/, '');
}

function compareEdges(a: QueryPlanSpec['edges'][number], b: QueryPlanSpec['edges'][number]): number {
  return a.to.localeCompare(b.to) || a.from.localeCompare(b.from);
}

function validSemanticId(id: string): boolean {
  return /^[a-z][a-z0-9-]*$/.test(id);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function n(value: number): number {
  return Math.round(value * 10) / 10;
}
